import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const blogSnap = await getDocs(query(collection(db, "blogs"), orderBy("createdAt", "desc")));
        const all = blogSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBlogs(all);
        setRecent(all.slice(0, 5));
        const catSnap = await getDocs(collection(db, "blogCategories"));
        setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, []);

  const filtered = selectedCategory === "All"
    ? blogs
    : blogs.filter((b) => b.category === selectedCategory);

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Montserrat, sans-serif" }}>
      <div className="bg-[#f8f0f5] py-10 px-6 text-center border-b">
        <h1 className="text-3xl font-extrabold uppercase tracking-wide">Blog</h1>
        <p className="text-sm text-gray-500 mt-2">Stories, insights, and resources for mental health</p>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main Content */}
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-10 h-10 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-400">No posts found.</div>
            ) : (
              <div className="space-y-8">
                {filtered.map((blog) => (
                  <article key={blog.id} className="flex flex-col md:flex-row gap-6 border-b pb-8">
                    {blog.coverImage && (
                      <Link to={`/blog/${blog.id}`} className="shrink-0">
                        <div className="w-full md:w-[240px] aspect-[16/10] overflow-hidden rounded-lg bg-gray-100">
                          <img
                            src={blog.coverImage}
                            alt={blog.title}
                            className="w-full h-full object-cover hover:scale-105 transition duration-300"
                          />
                        </div>
                      </Link>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {blog.category && (
                          <span className="px-3 py-1 bg-pink-50 text-[#e84fa5] text-[10px] font-bold uppercase rounded-full">
                            {blog.category}
                          </span>
                        )}
                        <span className="text-[11px] text-gray-400">
                          {blog.createdAt?.toDate ? blog.createdAt.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""}
                        </span>
                      </div>
                      <Link to={`/blog/${blog.id}`}>
                        <h2 className="text-[18px] font-extrabold text-black hover:text-[#e84fa5] transition leading-snug mb-2">
                          {blog.title}
                        </h2>
                      </Link>
                      <p className="text-[13px] text-gray-500 leading-relaxed line-clamp-3">{blog.excerpt}</p>
                      <Link
                        to={`/blog/${blog.id}`}
                        className="inline-block mt-4 text-[12px] font-bold text-[#e84fa5] hover:underline"
                      >
                        Read More →
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-[280px] space-y-8">
            {/* Search */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-[13px] font-extrabold uppercase mb-3">Search</h3>
              <input
                type="text"
                placeholder="Search posts..."
                className="w-full border border-gray-200 rounded px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]"
              />
            </div>

            {/* Categories */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-[13px] font-extrabold uppercase mb-4">Categories</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setSelectedCategory("All")}
                    className={`text-[13px] hover:text-[#e84fa5] transition ${selectedCategory === "All" ? "text-[#e84fa5] font-bold" : "text-gray-600"}`}
                  >
                    All Posts ({blogs.length})
                  </button>
                </li>
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <button
                      onClick={() => setSelectedCategory(cat.name)}
                      className={`text-[13px] hover:text-[#e84fa5] transition ${selectedCategory === cat.name ? "text-[#e84fa5] font-bold" : "text-gray-600"}`}
                    >
                      {cat.name} ({blogs.filter((b) => b.category === cat.name).length})
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Posts */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-[13px] font-extrabold uppercase mb-4">Recent Posts</h3>
              <ul className="space-y-4">
                {recent.map((blog) => (
                  <li key={blog.id} className="flex gap-3">
                    {blog.coverImage && (
                      <img src={blog.coverImage} alt="" className="w-14 h-14 object-cover rounded shrink-0" />
                    )}
                    <div>
                      <Link to={`/blog/${blog.id}`} className="text-[12px] font-semibold text-black hover:text-[#e84fa5] transition leading-tight">
                        {blog.title}
                      </Link>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {blog.createdAt?.toDate ? blog.createdAt.toDate().toLocaleDateString() : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Blog;
